/**
 * Actions Claude may propose in the chat (Fase 6c). Claude only proposes: the
 * app shows each one as a card, and runs it only when the user confirms. Every
 * action that ran is logged with what it takes to undo it.
 *
 * Pages hidden from AI can't be changed, nor be the place where something new
 * goes, exactly like they can't be read.
 */
import {
  dueToken,
  normalTime,
  PRIORITY_EMOJI,
  type Id,
  type TaskPriority,
  type TaskStatus,
} from '@nexus/core';
import { ensureTaskIds, nexusSchema, updateTaskItem } from '@nexus/editor';
import { parseMarkdown } from '@nexus/markdown';
import type { Store } from '@nexus/store';
import {
  addTaskToPage,
  inboxPage,
  jsonToUpdate,
  loadTasks,
  mutatePageDoc,
  reindexPage,
  updateTask,
  type Task,
} from '@nexus/workspace';
import type { JSONContent } from '@tiptap/core';
import type { ToolContext } from './tools';

export const ACTION_NAMES = [
  'create_task',
  'update_task',
  'create_page',
  'append_to_page',
  'move_page',
] as const;
export type ActionName = (typeof ACTION_NAMES)[number];
export const isAction = (name: string): name is ActionName =>
  (ACTION_NAMES as readonly string[]).includes(name);

const STATUSES: TaskStatus[] = ['todo', 'doing', 'waiting', 'done', 'cancelled'];
const PRIORITIES = Object.keys(PRIORITY_EMOJI) as TaskPriority[];
const MAX_MARKDOWN = 20_000;

/** What the card shows; labels are translated by the app. */
export interface ActionPreview {
  action: ActionName;
  /** Main line: the task or page title. */
  title: string;
  details: { label: 'due' | 'priority' | 'status' | 'where' | 'from' | 'to'; value: string }[];
  /** Text that will be added (create_page, append_to_page). */
  markdown?: string;
}

/** A checked action, ready to run once confirmed. */
export interface CheckedAction {
  preview: ActionPreview;
  run: () => Promise<{ result: string; undo: Undo; pageId: Id | null }>;
}

export type Undo =
  | { action: 'create_task'; pageId: Id; taskId: string }
  | {
      action: 'update_task';
      task: Pick<Task, 'id' | 'kind' | 'pageId'>;
      before: { status: TaskStatus; due: string | null; priority: TaskPriority | null };
    }
  | { action: 'create_page'; pageId: Id }
  | { action: 'append_to_page'; pageId: Id; blocks: JSONContent[] }
  | { action: 'move_page'; pageId: Id; parentId: Id | null; afterId: Id | null };

export class ActionError extends Error {}

const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
const titleOf = (ctx: ToolContext, id: Id | null) =>
  id ? (ctx.scope.visible.get(id)?.title ?? '') || 'Untitled' : '';

function visiblePage(ctx: ToolContext, id: string, what = 'page_id'): Id {
  if (ctx.scope.excluded.has(id))
    throw new ActionError(`That page is private (hidden from AI): it can't be changed.`);
  if (!ctx.scope.visible.has(id)) throw new ActionError(`No page with ${what}=${id}.`);
  return id;
}

function date(v: unknown): string | null {
  const s = str(v);
  if (!s) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) throw new ActionError(`Dates are YYYY-MM-DD, got "${s}".`);
  return s;
}
function time(v: unknown): string | null {
  const s = str(v);
  if (!s) return null;
  if (!/^\d{1,2}:\d{2}$/.test(s)) throw new ActionError(`Times are HH:MM, got "${s}".`);
  return normalTime(s);
}
function priority(v: unknown): TaskPriority | null {
  const s = str(v);
  if (!s) return null;
  if (!PRIORITIES.includes(s as TaskPriority))
    throw new ActionError(`priority is one of ${PRIORITIES.join(', ')}.`);
  return s as TaskPriority;
}
function markdown(v: unknown, required: boolean): string {
  const s = typeof v === 'string' ? v.trim() : '';
  if (required && !s) throw new ActionError('markdown is empty.');
  if (s.length > MAX_MARKDOWN)
    throw new ActionError(`markdown is longer than ${MAX_MARKDOWN} characters.`);
  return s;
}

/** The checkbox text for a new task, in the Tasks notation. */
function taskLine(title: string, due: string | null, t: string | null, p: TaskPriority | null) {
  return [title, due ? `📅 ${dueToken(due, t)}` : null, p ? PRIORITY_EMOJI[p] : null]
    .filter(Boolean)
    .join(' ');
}

const blocksOf = (md: string): JSONContent[] =>
  ensureTaskIds(parseMarkdown(md).doc).doc.content ?? [];

/** Check a proposed action. Throws ActionError (the message goes back to Claude). */
export async function checkAction(
  ctx: ToolContext,
  name: string,
  input: unknown,
): Promise<CheckedAction> {
  const a = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  const { store } = ctx;

  switch (name) {
    case 'create_task': {
      const title = str(a.title).replace(/\s+/g, ' ');
      if (!title) throw new ActionError('title is empty.');
      const due = date(a.due);
      const t = due ? time(a.time) : null;
      const p = priority(a.priority);
      const where = str(a.page_id) ? visiblePage(ctx, str(a.page_id)) : null;
      return {
        preview: {
          action: name,
          title,
          details: [
            ...(due ? [{ label: 'due' as const, value: dueToken(due, t) }] : []),
            ...(p ? [{ label: 'priority' as const, value: p }] : []),
            { label: 'where', value: where ? titleOf(ctx, where) : 'Inbox' },
          ],
        },
        run: async () => {
          const pageId = where ?? (await inboxPage(store)).id;
          const taskId = await addTaskToPage(store, pageId, taskLine(title, due, t, p));
          return {
            result: `Created task_id=${taskId} in page_id=${pageId}.`,
            undo: { action: name, pageId, taskId },
            pageId,
          };
        },
      };
    }

    case 'update_task': {
      const id = str(a.task_id);
      const task = (await loadTasks(store)).find((x) => x.id === id);
      if (!task) throw new ActionError(`No task with task_id=${id}.`);
      visiblePage(ctx, task.pageId);
      const patch: { status?: TaskStatus; due?: string | null; priority?: TaskPriority | null } =
        {};
      if (a.status !== undefined && a.status !== null) {
        const s = str(a.status) as TaskStatus;
        if (!STATUSES.includes(s))
          throw new ActionError(`status is one of ${STATUSES.join(', ')}.`);
        patch.status = s;
      }
      if ('due' in a) {
        const d = a.due === null ? null : date(a.due);
        const t = d ? time(a.time) : null;
        patch.due = d ? (t ? `${d} ${t}` : d) : null;
      }
      if ('priority' in a) patch.priority = a.priority === null ? null : priority(a.priority);
      if (Object.keys(patch).length === 0) throw new ActionError('Nothing to change.');
      const before = {
        status: task.status,
        due: task.due ? (task.dueTime ? `${task.due} ${task.dueTime}` : task.due) : null,
        priority: task.priority,
      };
      return {
        preview: {
          action: name,
          title: task.title,
          details: [
            ...(patch.status
              ? [{ label: 'status' as const, value: `${task.status} → ${patch.status}` }]
              : []),
            ...(patch.due !== undefined
              ? [{ label: 'due' as const, value: `${before.due ?? '—'} → ${patch.due ?? '—'}` }]
              : []),
            ...(patch.priority !== undefined
              ? [
                  {
                    label: 'priority' as const,
                    value: `${task.priority ?? '—'} → ${patch.priority ?? '—'}`,
                  },
                ]
              : []),
            { label: 'where', value: task.sourceTitle || titleOf(ctx, task.pageId) },
          ],
        },
        run: async () => {
          await updateTask(store, task, patch);
          return {
            result: `Updated task_id=${task.id}.`,
            undo: {
              action: name,
              task: { id: task.id, kind: task.kind, pageId: task.pageId },
              before,
            },
            pageId: task.pageId,
          };
        },
      };
    }

    case 'create_page': {
      const title = str(a.title).replace(/\s+/g, ' ');
      if (!title) throw new ActionError('title is empty.');
      const parentId = str(a.parent_id) ? visiblePage(ctx, str(a.parent_id), 'parent_id') : null;
      const md = markdown(a.markdown, false);
      return {
        preview: {
          action: name,
          title,
          details: [{ label: 'where', value: parentId ? titleOf(ctx, parentId) : '—' }],
          ...(md ? { markdown: md } : {}),
        },
        run: async () => {
          const page = await store.createPage({ title, parentId });
          if (md) {
            const doc = { type: 'doc', content: blocksOf(md) };
            await store.appendContent(page.id, jsonToUpdate(doc));
            await reindexPage(store, page.id, doc);
          }
          return {
            result: `Created page_id=${page.id}.`,
            undo: { action: name, pageId: page.id },
            pageId: page.id,
          };
        },
      };
    }

    case 'append_to_page': {
      const pageId = visiblePage(ctx, str(a.page_id));
      const md = markdown(a.markdown, true);
      return {
        preview: {
          action: name,
          title: titleOf(ctx, pageId),
          details: [],
          markdown: md,
        },
        run: async () => {
          const blocks = blocksOf(md);
          await mutatePageDoc(store, pageId, (doc) => ({
            ...doc,
            content: [...(doc.content ?? []), ...blocks],
          }));
          return {
            result: `Added to page_id=${pageId}.`,
            undo: { action: name, pageId, blocks },
            pageId,
          };
        },
      };
    }

    case 'move_page': {
      const pageId = visiblePage(ctx, str(a.page_id));
      const parentId = str(a.parent_id) ? visiblePage(ctx, str(a.parent_id), 'parent_id') : null;
      const page = ctx.scope.visible.get(pageId)!;
      if (parentId === page.parentId) throw new ActionError('The page is already there.');
      const siblings = [...ctx.scope.visible.values()]
        .filter((p) => p.parentId === page.parentId)
        .sort((x, y) => (x.sortKey < y.sortKey ? -1 : 1));
      const afterId = siblings[siblings.findIndex((p) => p.id === pageId) - 1]?.id ?? null;
      return {
        preview: {
          action: name,
          title: page.title,
          details: [
            { label: 'from', value: page.parentId ? titleOf(ctx, page.parentId) : '—' },
            { label: 'to', value: parentId ? titleOf(ctx, parentId) : '—' },
          ],
        },
        run: async () => {
          const children = (await store.listPages()).filter((p) => p.parentId === parentId);
          try {
            await store.movePage(pageId, parentId, children.at(-1)?.id ?? null);
          } catch {
            throw new ActionError("A page can't be moved inside itself.");
          }
          return {
            result: `Moved page_id=${pageId}.`,
            undo: { action: name, pageId, parentId: page.parentId, afterId },
            pageId,
          };
        },
      };
    }

    default:
      throw new ActionError(`Unknown action: ${name}`);
  }
}

/** Compared in normal form (default attributes filled in), as the document stores them. */
const normal = (blocks: JSONContent[]) =>
  JSON.stringify(blocks.map((b) => nexusSchema().nodeFromJSON(b).toJSON()));
const sameBlocks = (a: JSONContent[], b: JSONContent[]) => normal(a) === normal(b);

/**
 * Undo a logged action. Returns false when it can no longer be undone
 * automatically (the text was changed since; use the page history instead).
 */
export async function undoAction(store: Store, undo: Undo): Promise<boolean> {
  switch (undo.action) {
    case 'create_task': {
      const done = await mutatePageDoc(store, undo.pageId, (doc) => {
        const next = updateTaskItem(doc, undo.taskId, () => []);
        if (!next) return null;
        // Drop a task list that is empty now.
        return {
          ...next,
          content: (next.content ?? []).filter(
            (b) => b.type !== 'taskList' || (b.content?.length ?? 0) > 0,
          ),
        };
      });
      return !!done;
    }
    case 'update_task': {
      const task = (await loadTasks(store)).find((t) => t.id === undo.task.id);
      if (!task) return false;
      // Status, date and priority as they were (also undoes a recurring task's move to its next date).
      await updateTask(store, undo.task, {
        status: undo.before.status,
        due: undo.before.due,
        priority: undo.before.priority,
      });
      return true;
    }
    case 'create_page': {
      const page = await store.getPage(undo.pageId);
      if (page && !page.trashedAt) await store.trashPage(undo.pageId);
      return true;
    }
    case 'append_to_page': {
      const done = await mutatePageDoc(store, undo.pageId, (doc) => {
        const content = doc.content ?? [];
        const n = undo.blocks.length;
        if (content.length < n || !sameBlocks(content.slice(-n), undo.blocks)) return null;
        const rest = content.slice(0, -n);
        return { ...doc, content: rest.length ? rest : [{ type: 'paragraph' }] };
      });
      return !!done;
    }
    case 'move_page': {
      const page = await store.getPage(undo.pageId);
      if (!page || page.trashedAt) return false;
      const siblings = (await store.listPages()).filter((p) => p.parentId === undo.parentId);
      const after =
        undo.afterId && siblings.some((p) => p.id === undo.afterId) ? undo.afterId : null;
      await store.movePage(undo.pageId, undo.parentId, after);
      return true;
    }
  }
}
